import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '~/utils/redux/hooks';
import { selectStaffFeatures, selectUser } from '~/thunks/auth/authSlice';
import { canAccessAdminRoute } from '~/utils/helper/adminAccess';
import { adminRoute } from '~/utils/routes/admin';

type DashboardLinkCardProps = {
  to: string;
  titleKey: string;
  descriptionKey: string;
};

const DashboardLinkCard = ({ to, titleKey, descriptionKey }: DashboardLinkCardProps) => {
  const { t } = useTranslation();

  return (
    <Link
      to={to}
      className='flex flex-col gap-1 rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary sm:p-6'
    >
      <h2 className='text-base font-semibold text-foreground'>{t(titleKey)}</h2>
      <p className='text-sm text-muted-foreground'>{t(descriptionKey)}</p>
    </Link>
  );
};

// The pages the dashboard links to, each with the staff feature that grants it.
const CARDS = [
  { page: 'funnel', feature: 'FUNNEL' },
  { page: 'protectMode', feature: 'PROTECT_MODE' },
  { page: 'permissions', feature: 'PERMISSIONS' },
  { page: 'calls', feature: 'CALLS' },
  { page: 'location', feature: 'LOCATION' },
  { page: 'safetyAlerts', feature: 'SAFETY_ALERTS' },
  { page: 'errorLogs', feature: 'ERROR_LOGS' },
  { page: 'supportRequests', feature: 'SUPPORT_REQUESTS' },
] as const;

const Dashboard = () => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const staffFeatures = useAppSelector(selectStaffFeatures);

  const cards = CARDS.filter((card) => canAccessAdminRoute(user, staffFeatures, { feature: card.feature }));

  return (
    <div>
      <h1 className='text-xl font-semibold text-foreground'>{t('pages.dashboard.title')}</h1>
      <p className='mt-1 text-sm text-muted-foreground'>{t('pages.dashboard.description')}</p>

      {cards.length === 0 && <p className='mt-6 text-sm text-muted-foreground'>{t('pages.dashboard.noAccess')}</p>}

      <div className='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
        {cards.map((card) => (
          <DashboardLinkCard
            key={card.page}
            to={`${adminRoute.base}${adminRoute[card.page]}`}
            titleKey={`pages.${card.page}.title`}
            descriptionKey={`pages.${card.page}.description`}
          />
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
